import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import CrmLayout from '@/modules/crm/components/CrmLayout';
import ContactForm from '@/modules/crm/components/ContactForm';
import OpportunityCard from '@/modules/crm/components/OpportunityCard';
import { useCrmContact, useUpdateContact, useDeleteContact, useCrmSourceOptions } from '@/hooks/queries';
import { useToast } from '@/hooks/useToast';

export default function CrmContactDetailPage() {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const { toasts, showToast, hideToast } = useToast();
  
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const { data: contact, isLoading, error } = useCrmContact(contactId);
  const { data: sourceOptionsData } = useCrmSourceOptions();
  const updateMutation = useUpdateContact();
  const deleteMutation = useDeleteContact();
  
  // Source values are now human-readable, so just return the value directly
  const getSourceLabel = useMemo(() => {
    return (source) => source || '-';
  }, []);

  const handleUpdate = (formData) => {
    updateMutation.mutate(
      { contactId, ...formData },
      {
        onSuccess: () => {
          showToast('Contact updated', 'success');
          setIsEditing(false);
        },
        onError: (error) => {
          showToast(error.message || 'Failed to update contact', 'error');
        },
      }
    );
  };

  const handleDelete = () => {
    deleteMutation.mutate(contactId, {
      onSuccess: () => {
        showToast('Contact deleted', 'success');
        navigate('/crm/contacts');
      },
      onError: (error) => {
        showToast(error.message || 'Failed to delete contact', 'error');
      },
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getContactName = () => {
    if (!contact) return 'Unknown';
    const parts = [contact.firstName, contact.lastName].filter(Boolean);
    return parts.join(' ') || 'Unknown';
  };

  const getContactInitial = () => {
    return contact?.firstName?.charAt(0)?.toUpperCase() || '?';
  };

  if (isLoading) {
    return (
      <CrmLayout>
        <div className="bg-white rounded-lg border border-gray-200 p-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-48"></div>
            <div className="h-4 bg-gray-200 rounded w-32"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </CrmLayout>
    );
  }

  if (error || !contact) {
    return (
      <CrmLayout>
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <p className="text-gray-500 mb-4">Contact not found</p>
          <Link
            to="/crm/contacts"
            className="text-zapt-purple hover:text-zapt-purple/80 font-medium"
          >
            Back to contacts
          </Link>
        </div>
      </CrmLayout>
    );
  }

  return (
    <CrmLayout toasts={toasts} onCloseToast={hideToast}>
      {/* Back Button */}
      <button
        onClick={() => navigate('/crm/contacts')}
        className="inline-flex items-center text-zapt-purple hover:text-zapt-purple/80 font-medium mb-6"
      >
        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to Contacts
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Header Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-zapt-purple/10 rounded-full flex items-center justify-center">
                  <span className="text-zapt-purple text-2xl font-bold">
                    {getContactInitial()}
                  </span>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{getContactName()}</h1>
                  {contact.company && (
                    <p className="text-gray-600">{contact.company}</p>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Edit
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-4 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                >
                  Delete
                </button>
              </div>
            </div>

            {/* Contact Details */}
            {isEditing ? (
              <ContactForm
                contact={contact}
                onSubmit={handleUpdate}
                onCancel={() => setIsEditing(false)}
                isSubmitting={updateMutation.isPending}
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">
                    {contact.email ? (
                      <a href={`mailto:${contact.email}`} className="text-zapt-purple hover:underline">
                        {contact.email}
                      </a>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium">
                    {contact.phone ? (
                      <a href={`tel:${contact.phone}`} className="text-zapt-purple hover:underline">
                        {contact.phone}
                      </a>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Source</p>
                  <p className="font-medium">{getSourceLabel(contact.source)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Last Contacted</p>
                  <p className="font-medium">{formatDate(contact.lastContactedAt)}</p>
                </div>
                
                {/* Next Action */}
                {contact.nextAction && (
                  <div className="md:col-span-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-yellow-800">Next Action</p>
                    <p className="text-yellow-900">{contact.nextAction}</p>
                    {contact.nextActionDue && (
                      <p className={`text-sm mt-1 ${new Date(contact.nextActionDue) < new Date() ? 'text-red-600 font-medium' : 'text-yellow-700'}`}>
                        Due: {formatDate(contact.nextActionDue)}
                      </p>
                    )}
                    {contact.actionOwnerEmail && (
                      <p className="text-sm text-yellow-700">Owner: {contact.actionOwnerEmail}</p>
                    )}
                  </div>
                )}
                
                {contact.notes && (
                  <div className="md:col-span-2">
                    <p className="text-sm text-gray-500">Notes</p>
                    <p className="font-medium whitespace-pre-wrap">{contact.notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Opportunities Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Opportunities</h3>
              <Link
                to={`/crm/opportunities/new?contactId=${contactId}`}
                className="px-3 py-1.5 text-sm bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90"
              >
                + Add Opportunity
              </Link>
            </div>
            
            {contact.opportunities?.length > 0 ? (
              <div className="space-y-3">
                {contact.opportunities.map((opp) => (
                  <OpportunityCard key={opp.id} opportunity={opp} />
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No opportunities yet. Add one to track potential deals and interactions.</p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Linked User */}
          {contact.linkedUserId && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Linked User</h3>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 text-sm">✓</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">{contact.linkedUserEmail}</p>
                  <p className="text-xs text-gray-500">Signed up as user</p>
                </div>
              </div>
            </div>
          )}

          {/* Created By */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Created By</h3>
            <p className="text-gray-900">{contact.createdByEmail || 'Unknown'}</p>
            <p className="text-xs text-gray-500">{formatDate(contact.createdAt)}</p>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Contact</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <strong>{getContactName()}</strong>? This will also delete all associated opportunities and interactions. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </CrmLayout>
  );
}
